import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from 'react-redux'
import { Modal, Checkbox, Button, Progress, Space, Typography, Alert, Collapse } from "antd";
import JSZip, { forEach } from "jszip";
import { AdmsAsset, ModelViews} from '../../redux';
import { lastElementCardURI } from '../../utils/uriUtil';
import { collectGraphsFromSPARQL } from './exportGraph';

const { Text, Link, Title } = Typography;

const OPT = {
  CARD: "card_asset",
  WIKI: "wiki",
  DIST: "distribution",
  CLASS: "classification",
  RELATED: "relatedRefs",
  VIEWS: "views",
  GRAPHS: "graphs",
};

const LABELS = {
  [OPT.WIKI]    : "Включить в экспорт информацию о wiki-статьях",
  [OPT.DIST]    : "Включить в экспорт информацию о распространении актива",
  [OPT.CLASS]   : "Включить в экспорт детальную информацию о классификционных признаках",
  [OPT.RELATED] : "Включить в экспорт детальную информацию о связанных элементах из справочниках объектных свойств",
  [OPT.VIEWS]   : "Включить визуальные представления",
  [OPT.GRAPHS]   : "Выгрузить графы, указанные в представлениях"
};

const defaultChecked = [OPT.WIKI, OPT.DIST, OPT.VIEWS];

function ExportCard({
  open,
  onClose,
  assetId = "http://localhost/card/Asset",
  values = [],
  wikies = [],
  distributions = [],
  vviews = {},
}) {
  const [checked, setChecked] = useState(defaultChecked);

  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState(null);

  const [zipBlob, setZipBlob] = useState(null);
  const [zipUrl, setZipUrl] = useState("");

  // --- NEW: "Выгрузить графы" state ---
  const [exportGraphs, setExportGraphs] = useState(false);
  const [isGraphChecking, setIsGraphChecking] = useState(false);
  const [graphProgress, setGraphProgress] = useState(0);
  const [graphStatusText, setGraphStatusText] = useState("");

  // --- NEW: Log ---
  const [logLines, setLogLines] = useState([]);
  const [logCollapsed, setLogCollapsed] = useState(true); // управляем авто-сворачиванием

  // локальное состояние графов (не мутируем props!)
  const [graphs, setGraphs] = useState([]);

  
  const downloadTriggeredRef = useRef(false);

  const isWikiesEmpty = useMemo(
    () => !Array.isArray(wikies) || wikies.length === 0, [wikies]
  );
  const isDistributionsEmpty = useMemo(
    () => !Array.isArray(distributions) || distributions.length === 0,[distributions]
  );

  const isVviewsEmpty = useMemo(
    () =>
      isPlainEmptyObject(vviews) ||
      !Array.isArray(vviews["views"]) ||
      vviews["views"].length < 1,
    [vviews]
  );

  const isExportDone = !!zipUrl;

  const zipFileName = useMemo(() => {
    const fileName = lastElementCardURI(assetId);
    const stamp = new Date().toISOString().replaceAll(":", "-").replaceAll(".", "-");
    return `export_${fileName}_${stamp}.zip`;
  }, [assetId]);

  const viewsEnabled = checked.includes(OPT.VIEWS) && !isVviewsEmpty;
  const exportGraphsEnabled = viewsEnabled && !isExporting && !isExportDone;

  // --- Cleanup object URL ---
  useEffect(() => {
    return () => {
      if (zipUrl) URL.revokeObjectURL(zipUrl);
    };
  }, [zipUrl]);

  // При открытии: дефолты, убрать недоступные пункты
  useEffect(() => {
    if (!open) return;

    setChecked(() => {
      const next = new Set(defaultChecked);
      if (isWikiesEmpty) next.delete(OPT.WIKI);
      if (isDistributionsEmpty) next.delete(OPT.DIST);
      if (isVviewsEmpty) next.delete(OPT.VIEWS);
      return Array.from(next);
    });

    // NEW: reset exportGraphs when open
    setExportGraphs(false);

    resetRuntimeState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Если данные “пропали” — снять галочку
  useEffect(() => {
    if (!open) return;
    if (!isWikiesEmpty) return;
    setChecked((prev) => prev.filter((k) => k !== OPT.WIKI));
  }, [isWikiesEmpty, open]);

  useEffect(() => {
    if (!open) return;
    if (!isDistributionsEmpty) return;
    setChecked((prev) => prev.filter((k) => k !== OPT.DIST));
  }, [isDistributionsEmpty, open]);

  useEffect(() => {
    if (!open) return;
    if (!isVviewsEmpty) return;
    setChecked((prev) => prev.filter((k) => k !== OPT.VIEWS));
  }, [isVviewsEmpty, open]);

  // NEW: если отключили "визуальные представления" — принудительно снять "выгрузить графы"
  useEffect(() => {
    if (!viewsEnabled) {
      setExportGraphs(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewsEnabled]);

  function resetRuntimeState() {
    setIsExporting(false);
    setProgress(0);
    setStatusText("");
    setError(null);
    downloadTriggeredRef.current = false;

    // NEW: reset graph check
    setIsGraphChecking(false);
    setGraphProgress(0);
    setGraphStatusText("");

    // NEW: reset log
    setLogLines([]);
    setLogCollapsed(true);

    if (zipUrl) {
      URL.revokeObjectURL(zipUrl);
      setZipUrl("");
    }
  }

  function pushLog(line) {
    const ts = new Date().toISOString().slice(11, 19);
    setLogLines((prev) => [...prev, `[${ts}] ${line}`]);
  }

  // --- tasks for export ---
  const tasks = useMemo(() => {
    const selected = new Set(checked);
    const list = [];

    const IRIs = [assetId];

    if (selected.has(OPT.WIKI) && Array.isArray(wikies)) {
      wikies.forEach(wiki => IRIs.push(wiki.object.value));
    }
    if (selected.has(OPT.DIST) && Array.isArray(distributions)) {
      distributions.forEach(distribution => IRIs.push(distribution.object.value));
    }

    if (selected.has(OPT.CLASS) && Array.isArray(values)) {
      values
        .filter(triple => triple.predicat.value === "http://www.w3.org/ns/dcat#theme")
        .forEach(triple => IRIs.push(triple.object.value));
    }
    if (selected.has(OPT.RELATED) && Array.isArray(values)) {
      values
        .filter(triple => triple.object.type === "uri" && triple["labels"])
        .forEach(triple => IRIs.push(triple.object.value));
    }

    list.push({
      key: OPT.CARD,
      info: {"asset": assetId, "IRIs": IRIs},
      id: lastElementCardURI(assetId),
      title: "Получение данных",
      run: () => AdmsAsset.exportCard(undefined, IRIs),
    });

    if (selected.has(OPT.VIEWS) && !isVviewsEmpty) {
      vviews.views.forEach((view) => {
        list.push({
          key: OPT.VIEWS,
          info: {"asset": assetId, "view": view.id},
          id: view.id,
          title: view.view_title,
          run: () => vviews.model.getView(view.id),
        });
     });
    };

    if (selected.has(OPT.GRAPHS) && !isVviewsEmpty) {
      graphs.forEach(graph => {
        list.push({
          key: OPT.GRAPHS,
          info: {"asset": assetId, "graph": graph},
          id: iriToTTLFilenameUnique(graph),
          title: "Данные из "+graph,
          run: () => AdmsAsset.exportGraph(graph),
        });
      });
    };

    return list;
  }, [checked, assetId, values, wikies, distributions, vviews, isVviewsEmpty, graphs]);

  // --- NEW: graph export workflow ---
  async function runGraphsCheck() {
    if (!viewsEnabled) return;

    setIsGraphChecking(true);
    setGraphProgress(0);
    setGraphStatusText("Подготовка…");
    setLogCollapsed(false);

    try {
      const views = Array.isArray(vviews?.views) ? vviews.views : [];
      const total = Math.max(views.length, 1);
      
      const allGraphs = [];

      pushLog("Старт проверки графов (Virtuoso) по визуальным представлениям.");

      for (let i = 0; i < views.length; i += 1) {
        const viewMeta = views[i];
        const viewId = viewMeta.id;

        setGraphStatusText(`Чтение описания view ${viewId}…`);
        pushLog(`getView(${viewId})`);

        // 1) чтение описания
        const viewJson = await vviews.model.getView(viewId);

        // 2) анализ JSON -> достать IRI графа
        const graphsInView = extractGraphIri(viewJson["view"]);

        graphsInView.forEach (graphIri => 
          pushLog (graphIri? `Извлечён graphIri: ${graphIri}`: `graphIri не найден (view=${viewId})`)
        );
        // 3) проверка наличия графа в Virtuoso
        if (graphsInView.length<1){
          pushLog(`Пропуск проверки Virtuoso: нет graphs (view=${viewId})`);
        } else{
          for (let index = 0; index < graphsInView.length; ++index) {
            const graphIri = graphsInView[index];
            setGraphStatusText(`Проверка графа в Virtuoso: ${graphIri}…`);
            console.log("ExportCard.js", "checkVirtuosoGraphExists", graphIri);
            const exists = await checkVirtuosoGraphExists(graphIri);
            pushLog(`Virtuoso: graph ${exists ? "есть" : "НЕТ"} (${graphIri})`);            
            if (exists && !allGraphs.includes(graphIri))
              allGraphs.push(graphIri);
          }
        }
        console.log("ExportCard.js", "extractGraphIri", allGraphs);

        const pct = Math.round(((i + 1) / total) * 100);
        setGraphProgress(pct);
      }

      setGraphStatusText("Проверка графов завершена.");
      pushLog("Проверка графов завершена.");
      setGraphs(allGraphs);
    } catch (e) {
      pushLog(`Ошибка проверки графов: ${e?.message || String(e)}`);
    } finally {
      // после выполнения всех промисов прогресс бар скрывается
      setIsGraphChecking(false);
      setGraphProgress(0);
      setGraphStatusText("");
    }
      
  }

  // NEW: при переключении чекбокса "Выгрузить графы" запускаем проверку
  async function onToggleExportGraphs(next, checked, setChecked) {
    setExportGraphs(next);
    toggleChecked(OPT.GRAPHS, next, checked, setChecked)

    if (next) {
      await runGraphsCheck();
    } else {
      // просто логируем, прогресс уже скрыт
      pushLog("Выгрузка/проверка графов отключена пользователем.");
    }
  }

  async function handleExport() {
    setError(null);
    setIsExporting(true);
    setProgress(0);
    setStatusText("Подготовка…");
    setZipBlob(null);

    // лог разворачиваем
    setLogCollapsed(false);
    pushLog("Старт экспорта ZIP.");

    if (zipUrl) {
      URL.revokeObjectURL(zipUrl);
      setZipUrl("");
    }

    try {
      const lines = [];
      const info = [];
      const zip = new JSZip();

      const headerLines = [
        `Export for asset: ${assetId}`,
        `Generated at: ${new Date().toISOString()}`,
        `Selected sections: ${checked.length ? checked.join(", ") : "(none)"}`,
        "",
      ];
      lines.push(...headerLines);

      const total = tasks.length;
      for (let i = 0; i < tasks.length; i += 1) {
        const t = tasks[i];
        setStatusText(`Экспорт: ${t.title}…`);
        pushLog(`Экспорт: ${t.title}`);

        const result = await t.run();

        lines.push(`=== ${t.title} ===`);

        if (t.key === OPT.CARD) {
          const file = `${t.key}.ttl`;
          info.push({"card":t.info, "file":file});
          zip.file(file, `${result}\n`);
          pushLog(`Добавлен файл: ${file}`);
        }

        if (t.key === OPT.VIEWS) {
          const file = `views/${t.id}.json`;
          info.push({"view":t.info, "file": file});
          const viewStr = JSON.stringify(result);
          zip.file(file, `${viewStr}\n`);
          pushLog(`Добавлен файл: ${file} (${viewStr.length} байт строкой)`);
        }

        if (t.key === OPT.GRAPHS) {
          const file = `graph/${t.id}.ttl`;
          info.push({"graph":t.info, "file":file});
          zip.file(file, `${result}\n`);
          pushLog(`Добавлен файл: ${file}`);
        }

        lines.push("");

        const pct = Math.round(((i + 1) / total) * 90);
        setProgress(pct);
      }

      setStatusText("Упаковка ZIP…");
      pushLog("Упаковка ZIP…");

      zip.file("export.log", lines.join("\n"));
      zip.file("export.json", JSON.stringify(info));

      const blob = await zip.generateAsync({ type: "blob" }, (meta) => {
        const pct = 90 + Math.round((meta.percent / 100) * 10);
        setProgress(Math.min(100, Math.max(90, pct)));
      });

      setZipBlob(blob);

      const url = URL.createObjectURL(blob);
      setZipUrl(url);
      setStatusText("ZIP готов. Начинаю скачивание…");
      setProgress(100);

      pushLog(`ZIP сформирован: ${zipFileName}`);
      triggerDownload(url, zipFileName);
      downloadTriggeredRef.current = true;

      setIsExporting(false);
      setStatusText("Готово.");
      pushLog("Экспорт завершён.");

      // После окончания экспорта лог свёртывается
      setLogCollapsed(true);

    } catch (e) {
      setIsExporting(false);
      setProgress(0);
      setStatusText("");
      setError(e?.message || String(e));
      pushLog(`Ошибка экспорта: ${e?.message || String(e)}`);
      // при ошибке можно оставить лог раскрытым
      setLogCollapsed(false);
    }
  }

  function handleClose() {
    resetRuntimeState();
    onClose?.();
  }

  const primaryButtonLabel = isExportDone ? "Закрыть" : "Экспорт";
  const primaryOnClick = isExportDone ? handleClose : handleExport;

  // Показывать блок прогресса, если идёт экспорт или идёт проверка графов или экспорт завершён
  const showProgressBlock = isExporting || isGraphChecking || isExportDone;

  const logItems = [
    {
      key: "log",
      label: "Журнал выполнения",
      children: (
        <div
          style={{
            maxHeight: 220,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            fontSize: 12,
            lineHeight: 1.35,
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 10,
          }}
        >
          {logLines.length ? logLines.join("\n") : "Лог пуст."}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={<Title level={5} style={{ margin: 0 }}>Экспорт</Title>}
      open={open}
      onCancel={handleClose}
      maskClosable={!isExporting && !isGraphChecking}
      closable={!isExporting && !isGraphChecking}
      destroyOnHidden={false}
      footer={
        <Space>
          {!isExportDone ? (
            <Button onClick={handleClose} disabled={isExporting || isGraphChecking}>
              Отмена
            </Button>
          ) : null}
          <Button
            type="primary"
            onClick={primaryOnClick}
            loading={isExporting}
            disabled={isExporting || isGraphChecking || (!zipUrl && tasks.length === 0)}
          >
            {primaryButtonLabel}
          </Button>
        </Space>
      }
    >
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Text type="secondary">
          Выберите, какие разделы включить в экспорт:
        </Text>

        <Space orientation="vertical" size={8} style={{ width: "100%" }}>
          <Checkbox
            checked={checked.includes(OPT.WIKI)}
            onChange={(e) => toggleChecked(OPT.WIKI, e.target.checked, checked, setChecked)}
            disabled={isExporting || isGraphChecking || isExportDone || isWikiesEmpty }
          >
            {LABELS[OPT.WIKI]}
          </Checkbox>

          <Checkbox
            checked={checked.includes(OPT.DIST)}
            onChange={(e) => toggleChecked(OPT.DIST, e.target.checked, checked, setChecked)}
            disabled={isExporting || isGraphChecking || isExportDone || isDistributionsEmpty }
          >
            {LABELS[OPT.DIST]}
          </Checkbox>

          <Checkbox
            checked={checked.includes(OPT.CLASS)}
            onChange={(e) => toggleChecked(OPT.CLASS, e.target.checked, checked, setChecked)}
            disabled={isExporting || isGraphChecking || isExportDone }
          >
            {LABELS[OPT.CLASS]}
          </Checkbox>

          <Checkbox
            checked={checked.includes(OPT.RELATED)}
            onChange={(e) => toggleChecked(OPT.RELATED, e.target.checked, checked, setChecked)}
            disabled={isExporting || isGraphChecking || isExportDone }
          >
            {LABELS[OPT.RELATED]}
          </Checkbox>

          <Checkbox
            checked={checked.includes(OPT.VIEWS)}
            onChange={(e) => toggleChecked(OPT.VIEWS, e.target.checked, checked, setChecked)}
            disabled={isExporting || isGraphChecking || isExportDone || isVviewsEmpty}
          >
            {LABELS[OPT.VIEWS]}
          </Checkbox>

          {/* NEW: Выгрузить графы */}
          <Checkbox
            checked={exportGraphs}
            onChange={(e) => onToggleExportGraphs(e.target.checked, checked, setChecked)}
            disabled={!exportGraphsEnabled}
            style={{ marginLeft: 24 }}
          >
            {LABELS[OPT.GRAPHS]}            
          </Checkbox>
        </Space>

        {showProgressBlock ? (
          <Space orientation="vertical" style={{ width: "100%" }}>
            {/* Экспортный прогресс */}
            {(isExporting || isExportDone) ? (
              <>
                <Progress percent={progress} />
                {statusText ? <Text>{statusText}</Text> : null}
              </>
            ) : null}

            {/* Прогресс проверки графов (показывается только пока идёт) */}
            {isGraphChecking ? (
              <>
                <Progress percent={graphProgress} />
                {graphStatusText ? <Text>{graphStatusText}</Text> : null}
              </>
            ) : null}

            {/* Лог под прогрессбаром */}
            <Collapse
              items={logItems}
              activeKey={logCollapsed ? [] : ["log"]}
              onChange={(keys) => setLogCollapsed(!(Array.isArray(keys) ? keys.includes("log") : false))}
            />
          </Space>
        ) : null}

        {error ? (
          <Alert
            type="error"
            showIcon
            message="Ошибка экспорта"
            description={<Text>{error}</Text>}
          />
        ) : null}

        {isExportDone ? (
          <Space orientation="vertical" style={{ width: "100%" }}>
            <Text strong>Ссылка на скачивание:</Text>
            <Link href={zipUrl} download={zipFileName}>
              Скачать {zipFileName}
            </Link>
            <Text type="secondary">
              Если скачивание не началось автоматически, нажмите ссылку выше.
            </Text>
          </Space>
        ) : null}
      </Space>
    </Modal>
  );
}

// --- helpers ---

function toggleChecked(key, next, checked, setChecked) {
  const set = new Set(checked);
  if (next) set.add(key);
  else set.delete(key);
  setChecked(Array.from(set));
}

function isPlainEmptyObject(v) {
  if (!v || typeof v !== "object") return true;
  if (Array.isArray(v)) return v.length === 0;
  return Object.keys(v).length === 0;
}

function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Пытаемся извлечь IRI графов из JSON описания представления.
 */
function extractGraphIri(view) {  
  if (!view || typeof view !== "object") return [];

  if (view["type"] === "table"){
    const src_query = view["src_query"];
    return collectGraphsFromSPARQL(src_query);
  };
  
  if (view["type"] === "cmp"){
    const src_query = view["src_query"];
    return collectGraphsFromSPARQL(src_query);
  };

  if (view["type"] === "tree"){
    const src_query = view["src_query"];
    return collectGraphsFromSPARQL(src_query);
  };

  if (view["type"] === "list"){
    const src_query = view["src_query"];
    return collectGraphsFromSPARQL(src_query);
  };

  if (view["type"] === "graph"){
    const src_query = view["sparql"];
    const graph = view["graph"];      
    const graphs = collectGraphsFromSPARQL(src_query);
    if (graph &&  graph !== "")
      graphs.push(graph);
    return graphs;
  };

  return [];
}

async function checkVirtuosoGraphExists(graphIri) {  
  try {
    const { state } = await AdmsAsset.checkGraphState2(graphIri);
    return state === "present";
  } catch {
    return false;
  }
}

function iriToTTLFilenameUnique(iri) {
  return `${encodeURIComponent(String(iri ?? "")).replace(/%/g, "_")}`;
}

// Redux mapping (как у тебя)
const mapStateToProps = (state, ownProps) => {
  const { vasset } = state;
  return {
    ...ownProps,
    assetId: ownProps.assetId ?? vasset.iri,
    values: vasset.values ?? [],
    wikies: vasset.wikies ?? [],
    distributions: vasset.distributions ?? [],
    vviews: ownProps.vviews ?? {},
  };
};

export default connect(mapStateToProps)(ExportCard);
